import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'react-native-uuid';
import { Alert } from '@/common/components/Alert/Alert';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { Modal } from '@/common/components/Modal/Modal';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { MARKET } from '@/common/constants/markets';
import { ROUTES } from '@/common/constants/routes';
import { useInsuranceContext } from '@/common/contexts/insurance/InsuranceContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { transformMenhirPlanSimulationPerson } from '@/common/integrations/menhir/employee/insurance/insurance.dto';
import { useCreatePlanSimulation } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { TMemberSimulation } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { notify } from '@/common/utils/toast/toast';
import { useMarket } from '@/web/hooks/useMarket';
import { moneyToString } from '@coverflex-tech/hypernova-utils';
import { useRouter } from 'expo-router';
import { SimulateAddFamilyMemberForm } from './SimulateAddFamilyMemberForm/SimulateAddFamilyMemberForm';
import { SimulateFamilyMembersList } from './SimulateFamilyMembersList/SimulateFamilyMembersList';

export const SimulateFamilyMembersModal = ({
  onClose,
  policyId,
  showSavingsModal,
}: TSimulateFamilyMembersModalProps) => {
  const { t } = useTranslation();
  const { push } = useRouter();
  const modalRef = useRef<TOverlayRef>(null);
  const { simulation, setSimulation } = useInsuranceContext();
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const { mutateAsync } = useCreatePlanSimulation();
  const { slug } = useMarket();
  const selectedPersons = simulation.persons.filter(
    (person) => person.selected
  );
  const [ready, setReady] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const canPayWithBenefits = simulation?.holderPaymentMethodsAllowed.find(
    (method) => method === 'wallet'
  );

  const onToggle = (personToUpdate: number) => {
    const persons = simulation.persons.map((person, index) =>
      personToUpdate === index
        ? { ...person, selected: !person.selected }
        : person
    );
    setSimulation({ ...simulation, persons });
    updateSimulation(persons);
  };

  const onRemove = (personToRemove: number) => {
    const persons = simulation.persons.filter(
      (_, index) => index !== personToRemove
    );
    setSimulation({ ...simulation, persons });
    updateSimulation(persons);
  };

  const onAddMember = (newPerson: TMemberSimulation) => {
    const updatedPersons = [
      ...simulation.persons,
      { ...newPerson, id: uuid.v4() },
    ];
    updateSimulation(updatedPersons);
  };

  const updateSimulation = async (persons: TMemberSimulation[]) => {
    try {
      setIsCalculating(true);
      const updatedSimulation = await mutateAsync({
        policyId: policyId,
        persons: persons.map(transformMenhirPlanSimulationPerson),
      });

      if (updatedSimulation) {
        setSimulation(updatedSimulation);
      }
      setShowAddMemberForm(updatedSimulation.persons.length === 0);
    } catch (error) {
      notify({
        message: (
          <Translate
            i18nKey="dashboard.insurance_detail.add_family_members.confirm.error.message"
            components={{
              reach_out: (
                <TextAction bold onPress={() => AnalyticsService.openChat()} />
              ),
            }}
          />
        ),
        type: 'error',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const onCloseForm = () => {
    setShowAddMemberForm(false);
    if (selectedPersons.length === 0) {
      modalRef.current?.close();
    }
  };

  useEffect(() => {
    const fetchSimulation = async () => {
      try {
        const response = await mutateAsync({
          policyId: policyId,
          persons: simulation.persons.map(transformMenhirPlanSimulationPerson),
        });
        setReady(true);
        setSimulation(response);
        if (response.persons.length === 0) {
          setShowAddMemberForm(true);
        }
      } catch {
        notify({
          message: t('errors.api.generic'),
          type: 'error',
        });
      }
    };

    if (!ready) {
      fetchSimulation();
    }
  }, [ready, simulation, mutateAsync, policyId, setSimulation, t]);

  const modalFooter = (
    <Stack gap="md">
      <Stack horizontal gap="md" shrink>
        <Stack>
          <Label color="neutral20">{t('general.labels.paid_by_you')}</Label>
          <Label size="xl" bold>
            {isCalculating
              ? t('general.labels.calculating')
              : moneyToString(simulation?.totalCost)}
          </Label>
        </Stack>

        {!isCalculating &&
          slug === MARKET.PT &&
          canPayWithBenefits &&
          simulation.savingsAmount?.amount > 0 &&
          simulation.totalCost?.amount > 0 && (
            <Alert type="info" noBackground>
              <Translate
                i18nKey="dashboard.insurance_detail.simulate_family_modal.savings.alert"
                components={{
                  link_more: <TextAction onPress={() => showSavingsModal()} />,
                }}
                values={{
                  amount: moneyToString(simulation.savingsAmount),
                }}
              />
            </Alert>
          )}
      </Stack>
      <Button
        onPress={() => {
          push({
            pathname: ROUTES.ADD_INSURANCE_MEMBERS,
            params: {
              policyId: policyId,
            },
          });
          onClose();
        }}
        disabled={selectedPersons.length === 0 || isCalculating}
        icon={<Icon icon="user-plus" />}
      >
        {t(
          'dashboard.insurance_detail.simulate_family_modal.add_members.button'
        )}
      </Button>
    </Stack>
  );

  const showFooter = !showAddMemberForm && ready;

  return (
    <Modal
      title={t('dashboard.insurance_detail.simulate_family_modal.title')}
      iconBadgeColor="accentPrimary20"
      icon={<Icon icon="family-pants" />}
      onClose={onClose}
      backgroundColor="neutral20"
      heightClass="h-[55rem]"
      footer={showFooter && modalFooter}
      modalRef={modalRef}
    >
      {ready ? (
        <Stack gap="md">
          <SimulateFamilyMembersList
            simulatedPersons={simulation.persons}
            onRemove={onRemove}
            onToggle={onToggle}
            isLoading={isCalculating}
          />
          {showAddMemberForm ? (
            <SimulateAddFamilyMemberForm
              onClose={onCloseForm}
              onAddMember={onAddMember}
              hasSpouse={simulation.persons.some(
                (person) => person.type === 'spouse'
              )}
              loading={isCalculating}
            />
          ) : (
            <Button
              icon={<Icon icon="user-plus" />}
              variant="tertiary"
              onPress={() => setShowAddMemberForm(true)}
              disabled={isCalculating}
            >
              {t(
                'dashboard.insurance_detail.simulate_family_modal.add_member.button'
              )}
            </Button>
          )}
        </Stack>
      ) : (
        <LoadingPlaceholder />
      )}
    </Modal>
  );
};

export type TSimulateFamilyMembersModalProps = {
  onClose: () => void;
  policyId: string;
  showSavingsModal: () => void;
};
